import React, { useState, useContext } from "react";
import { useHistory } from "react-router-dom";
import {
  useTheme,
  CircularProgress,
  Typography,
  Box,
  Divider,
  List,
  ListItem,
  Stack,
  Link,
  Card,
  useMediaQuery,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { Helmet } from "react-helmet";
import GridContainer from "../../components/grid/GridContainer.jsx";
import GridItem from "../../components/grid/GridItem.jsx";
import { addPatientStyleStyle } from "../../components/customstyles/DashboardStyle.jsx";
import Button from "../../components/customcomponents/CustomButton.jsx";
import { Formik, Field, Form } from "formik";
import { AuthContext } from "../../context/AuthContext.jsx";
import { string, object } from "yup";
import FormikTextField from "../../components/formikcomponent/TextField.jsx";
import searchImage from "../../assets/images/searchImage.png";
import errorAddPatient from "../../assets/images/errorAddPatient.png";
const useStyles = () => {
  const theme = useTheme();
  return addPatientStyleStyle(theme);
};

const PATIENT_MMR_MAX_LEN = 16;
const SOMETHING_WENT_WRONG = "Sorry, something went wrong!";

function AddPatient() {
  const styles = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [currentPatientData, setCurrentPatientData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorState, setErrorState] = useState(false);
  const [errorTitle, setErrorTitle] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const history = useHistory();
  const { makeAuthenticatedApiCall } = useContext(AuthContext);

  const handleSubmit = async (values) => {
    setErrorState(false);
    setLoading(true);
    let response = await makeAuthenticatedApiCall(
      "get",
      `/api/userservice/getPatientDetailsByEHRID/${values.patientId}`
    );
    if (response?.status === 200 && response.data) {
      setCurrentPatientData(response.data);
    } else if (response?.status === 400) {
      setCurrentPatientData(null);
      if (response.data.errorCode === 1) {
        setErrorTitle(SOMETHING_WENT_WRONG);
        setErrorMessage("Error getting patient details");
        setErrorState(true);
      } else if (response.data.errorCode === 2) {
        setErrorTitle("No patients found");
        setErrorMessage(
          "We found no patients with this EHR Patient ID. Please double-check the EHR Patient ID and try again."
        );
        setErrorState(true);
      } else {
        console.log("[error search patient unhandled errorCode]", response);
        setErrorTitle(SOMETHING_WENT_WRONG);
        setErrorMessage(
          "There was an error loading this page. Please try again later."
        );
        setErrorState(true);
      }
    } else {
      console.log("[error search patient]", response);
    }
    setLoading(false);
  };
  const AddNewPatient = async () => {
    setErrorState(false);
    setLoading(true);
    let response = await makeAuthenticatedApiCall(
      "get",
      `/api/userservice/getpatientdetails/${currentPatientData.patientId}`
    );
    if (response?.status === 200 && response.data) {
      setCurrentPatientData(response.data);
      history.push(`/patients/${currentPatientData.patientId}/1`, {
        patientAdded: !currentPatientData?.existsInOurSystem,
      });
    } else if (response?.status === 400) {
      setCurrentPatientData(null);
      if (response.data.errorCode === 1) {
        setErrorTitle(SOMETHING_WENT_WRONG);
        setErrorMessage("Error getting patient details");
        setErrorState(true);
      } else if (response.data.errorCode === 2) {
        setErrorTitle("No patients found.");
        setErrorMessage(
          "We found no patients with this EHR Patient ID. Please double-check the EHR Patient ID and try again."
        );
        setErrorState(true);
      } else {
        setErrorTitle(SOMETHING_WENT_WRONG);
        setErrorMessage(
          "There was an error loading this page. Please try again later."
        );
        console.log("[error search patient unhandled errorCode]", response);
        setErrorState(true);
      }
    } else {
      console.log("[error search patient]");
    }
    setLoading(false);
  };

  return (
    <Card sx={styles.addPatientTableStyle}>
      <Helmet>
        <title>Add Patient</title>
      </Helmet>
      <Box>
        <GridContainer>
          {isMobile ? (
            <Box sx={styles.headerStyle}>
              <ChevronLeft color="black" />
              <Typography variant="p" sx={{ color: "#0D47A1" }}>
                Back to Patients List
              </Typography>
            </Box>
          ) : (
            <Box sx={styles.headerStyle}>
              <Link href="/patients" underline="none">
                <Typography
                  variant="body1"
                  sx={{ color: "#0D47A1", textDecoration: "underline" }}
                >
                  Patients List
                </Typography>
              </Link>
              <ChevronRight />
              <Typography variant="p">Add New Patient</Typography>
            </Box>
          )}
          <GridItem lg={12} md={12} sm={12} xs={12} sx={styles.gridAlign}>
            <Typography className="FSUnmask" sx={styles.headingStyle}>
              Add New Patient
            </Typography>
            <Typography className="FSUnmask" sx={styles.subHeadingStyle}>
              Please input an EHR Patient ID to add them to AIMS.
            </Typography>
            <Divider />
            <Formik
              initialValues={{ patientId: "" }}
              validationSchema={object({
                patientId: string()
                  .max(PATIENT_MMR_MAX_LEN, "Invalid EHR Patient ID.")
                  .matches(/^[a-zA-Z0-9]+$/, "Invalid EHR Patient ID.")
                  .required("This field is required."),
              })}
              onSubmit={handleSubmit}
            >
              {(props) => (
                <Form>
                  <GridContainer sx={styles.inlineStyle}>
                    <GridItem
                      lg={10}
                      md={10}
                      sm={9}
                      xs={9}
                      sx={styles.gridAlign}
                    >
                      <Field
                        name="patientId"
                        style={styles.inputItem}
                        variant="outlined"
                        label="EHR Patient ID"
                        component={FormikTextField}
                        onChange={() => setErrorState(false)}
                        InputProps={{
                          style: {
                            backgroundColor: "#fff",
                            minHeight: "47px",
                            fontSize: "14px",
                          },
                        }}
                      />
                    </GridItem>
                    <GridItem lg={2} md={2} sm={3} xs={3} sx={styles.gridAlign}>
                      <Button
                        primary
                        disabled={props.values.patientId.trim() === ""}
                        type="submit"
                        wrapperSx={styles.buttonStyle}
                        sx={styles.searchButtonStyle}
                      >
                        Find Patient
                      </Button>
                    </GridItem>
                  </GridContainer>
                </Form>
              )}
            </Formik>
          </GridItem>
        </GridContainer>

        {loading && (
          <Box sx={{ ...styles.loadingInsuranceStyle, top: "60%" }}>
            <Box sx={{ marginBottom: "20px" }}>
              <CircularProgress sx={{ color: "#00446B" }} />
            </Box>
            <Typography sx={styles.loadinText}>Loading...</Typography>
          </Box>
        )}
        {errorState && (
          <Stack
            justifyContent="center"
            alignItems="center"
            spacing={3}
            sx={{ height: "300px" }}
          >
            <img
              alt="add-patient-error"
              src={errorAddPatient}
              style={{
                width: "50px",
                height: "50px",
                marginBottom: "5px",
              }}
            />
            <Stack justifyContent="center" alignItems="center" spacing={1}>
              <Typography
                sx={{
                  fontWeight: 400 + "!important",
                  textAlign: "center",
                  fontSize: "24px",
                  lineHeight: "32px",
                  color: "#030404",
                }}
              >
                {errorTitle}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: "16px",
                  color: "#546E7A",
                  textAlign: "center",
                  padding: "0 20px",
                }}
              >
                {errorMessage}
              </Typography>
            </Stack>
          </Stack>
        )}
        {currentPatientData && (
          <List>
            <div>
              <ListItem>
                <GridContainer sx={styles.gridCAlign}>
                  <GridItem lg={5} md={5} sm={5} xs={5} sx={styles.gridAlign}>
                    <Typography sx={styles.subHeadingStyle}>
                      Patient Name
                    </Typography>
                  </GridItem>
                  <GridItem lg={5} md={5} sm={5} xs={5} sx={styles.gridAlign}>
                    <Typography sx={styles.subHeadingStyle}>
                      EHR Patient ID
                    </Typography>
                  </GridItem>
                  <GridItem
                    lg={2}
                    md={2}
                    sm={2}
                    xs={2}
                    sx={styles.gridAlign}
                  ></GridItem>
                  <GridItem
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    sx={{
                      borderBottom:
                        "1px solid var(--divider, rgba(0, 0, 0, 0.12))",
                    }}
                  ></GridItem>
                  <GridItem lg={5} md={5} sm={5} xs={5} sx={styles.gridAlign}>
                    <Typography sx={styles.subHeadingStyle}>
                      {currentPatientData.patientName}
                    </Typography>
                  </GridItem>
                  <GridItem lg={5} md={5} sm={5} xs={5} sx={styles.gridAlign}>
                    <Typography
                      sx={(theme) => ({
                        ...styles.subHeadingStyle,
                        color: theme.textColour.textTertiary,
                      })}
                    >
                      {currentPatientData.emrPatientId}
                    </Typography>
                  </GridItem>
                  <GridItem
                    lg={2}
                    md={2}
                    sm={2}
                    xs={2}
                    sx={{ textAlign: "right" }}
                  >
                    <Button
                      sx={styles.addPatientButtonMrn}
                      onClick={() => {
                        AddNewPatient();
                      }}
                    >
                      {currentPatientData?.existsInOurSystem ? (
                        <Typography sx={styles.addPatientButtonText}>
                          View Patient
                        </Typography>
                      ) : (
                        <Typography sx={styles.addPatientButtonText}>
                          Add Patient
                        </Typography>
                      )}
                    </Button>
                  </GridItem>
                </GridContainer>
              </ListItem>
            </div>
          </List>
        )}

        {!currentPatientData && !errorState && !loading && (
          <Stack
            justifyContent="center"
            alignItems="center"
            spacing={3}
            sx={{ height: "300px", textAlign: "center" }}
          >
            <GridContainer>
              <GridItem
                lg={12}
                md={12}
                sm={12}
                xs={12}
                sx={styles.searchPatientStyle}
              >
                <img
                  src={searchImage}
                  style={{
                    width: "50px",
                    height: "50px",
                    marginBottom: "20px",
                  }}
                  alt={""}
                />
              </GridItem>
              <GridItem
                lg={12}
                md={12}
                sm={12}
                xs={12}
                sx={styles.searchPatientStyle}
              >
                <Typography sx={styles.searchHeaderText}>
                  Search for patients
                </Typography>
                <Typography sx={styles.searchparagraphText}>
                  Add patients by searching for them with their EHR Patient ID.
                </Typography>
              </GridItem>
            </GridContainer>
          </Stack>
        )}
      </Box>
    </Card>
  );
}
export default AddPatient;
